// @flow
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import { NumericFormat } from "react-number-format";
import { Link } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FormBoxTitle from "../../../../components/mui/app/formBoxTitle/formBoxTitle";
import PageContentWrapper from "../../../../components/mui/app/pageContentWrapper/pageContentWrapper";
import QuickViewInfoWrap from "../../../../components/mui/app/quickViewInfoWrap/quickViewInfoWrap";
import BoolImage from "../../../../components/mui/general/boolImage/boolImage";
import SkeletonLoaderWrapper from "../../../../components/mui/general/skeletonLoaderWrapper/skeletonLoaderWrapper";
import { API } from "../../../../helpers/constants";
import { handleAxiosErrors } from "../../../../helpers/errorHandling";
import { issueIsClosed } from "../../../../helpers/helpers";
import {
	edit_closed_issue, quickview_view_actual_charge,
	quickview_view_assignee, quickview_view_charge,
	quickview_view_erp_code, quickview_view_estimated_end, quickview_view_financial_info,
	quickview_view_technicians, view_po
} from "../../../../helpers/rights/rights";
import { getRouteUrl } from "../../../../helpers/routing/getRouteUrl";
import { flexBoxSx } from "../../../../helpers/styles";
import useUserData from "../../../../hooks/user/useUserData";
import CardChip from "../../../../pages/dashboard/partials/cardChip";
import PermissionHOC from "../../../permission/components/permissionHOC/permissionHOC";
import { useHasPermission } from "../../../permission/hooks/useHasPermission";
import { ISSUES_FORM } from "../../locales/namespaces";
import { ROUTE_PAGE_ISSUES_EDIT } from "../../routes/routes";
import FollowUpButton from "../followUpButton/followUpButton";
import AttachmentsTable from "./attachmentsTable";
import ItemsTable from "./itemsTable";
import AssetsTable from "./assetsTable";
import NotesTable from "./notesTable";
import Dialog from "@mui/material/Dialog";
import PoQuickView from "../PO/poQuickView";
import SoCustQuickView from "../SOCust/soCustQuickView";
import PoCustQuickView from "../POcust/poCustQuickView";

type Props = {};

const IssueQuickView = (props: Props) => {
	const {
		issueId,
		onClose,
		loadingIndex,
		filters,
		dataManager,
		setRowSelectionModel
	} = props
	const { t } = useTranslation(ISSUES_FORM)
	const { token, dateFormat, timeFormat, decimalSeparator, thousandSeparator } = useUserData()
	const [issue, setIssue] = useState(null)
	const [openPO,setOpenPO] = useState(false)
	const [openSOCust,setOpenSOCust] = useState(false)
	const [openPOCust,setOpenPOCust] = useState(false)
	const editClosedIssue = useHasPermission([edit_closed_issue], { shouldView: true })
	useEffect(() => {
		if (issueId) {
			axios({
				method: 'post',
				url: API + '/en/issues/edit/' + issueId,
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
			}).then(res => {
				setIssue(res.data.issue?.[0])
			}).catch(error => {
				enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
					variant: 'error',
					autoHideDuration: 3000,
				})
			})
		}
	}, [issueId, t,])
	
	const displayEditButton = useMemo(() => (
		((issueIsClosed(issue?.StatusId) && editClosedIssue) || !issueIsClosed(issue?.StatusId))
	), [issue?.StatusId, editClosedIssue])
	
	return (
		<SkeletonLoaderWrapper loading={!Boolean(issue)}>
			<Box
				sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
			>
				<Box sx={{
					width: '100%',
					display: 'flex',
					justifyContent: 'space-between',
					flexWrap: "wrap-reverse",
					gap: 0.2
				}}>
					<Button
						component={Link}
						to={getRouteUrl(ROUTE_PAGE_ISSUES_EDIT, { IssueId: issueId })}
						target="_blank"
						disabled={!displayEditButton}
						size={'small'}
						variant={'contained'}
						sx={{
							width: {
								xs: '100%',
								sm: 'auto'
							}
						}}
					>
						<EditIcon fontSize="small"/>
						{t('Edit Issue')}
					</Button>
					<FollowUpButton issueId={issueId}/>
					<IconButton
						onClick={onClose}
						color={'error'}
						size={'small'}
						sx={{ marginLeft: 'auto' }}
					>
						<ClearIcon/>
					</IconButton>
				</Box>
				<PageContentWrapper>
					<Box>
						<Grid container spacing={2}>
							<Grid size={12}>
								<FormBoxTitle
									title={t('General Information')}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<QuickViewInfoWrap label={t('Code')} highLightValue>{issue?.Code}</QuickViewInfoWrap>
								<PermissionHOC
									requiredPermissions={[quickview_view_erp_code]}
								>
									<QuickViewInfoWrap label={t('Erp Code')}
													   highLightValue>{issue?.ErpCode}</QuickViewInfoWrap>
								</PermissionHOC>
								<QuickViewInfoWrap label={t('Ref. Code')}
												   highLightValue>{issue?.ErpCode2}</QuickViewInfoWrap>
								<PermissionHOC
									requiredPermissions={[view_po]}
								>
									<QuickViewInfoWrap label={t('PO Code')}
													   highLightValue>
										<Button
											variant="text"
											onClick={() => setOpenPO(true)}
											sx={{
												p: 0,
												minWidth: 'auto',
												fontWeight: 600,
												textTransform: 'none',
											}}
										>
											{issue?.POCode}
										</Button>
									</QuickViewInfoWrap>
									<QuickViewInfoWrap label={t('SO Code')}
													   highLightValue>
										<Button
											variant="text"
											onClick={() => setOpenSOCust(true)}
											sx={{
												p: 0,
												minWidth: 'auto',
												fontWeight: 600,
												textTransform: 'none',
											}}
										>
											{issue?.SOCode}
										</Button>
									</QuickViewInfoWrap>
									<QuickViewInfoWrap label={t('PO Cust Code')}
													   highLightValue>
										<Button
											variant="text"
											onClick={() => setOpenPOCust(true)}
											sx={{
												p: 0,
												minWidth: 'auto',
												fontWeight: 600,
												textTransform: 'none',
											}}
										>
											{issue?.CPOCode}
										</Button>
									</QuickViewInfoWrap>
								</PermissionHOC>
								<QuickViewInfoWrap label={t('Active')}>
									<BoolImage isChecked={issue?.isActive === '1'}/>
								</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Billable')}>
									<BoolImage isChecked={issue?.isBillable === '1'}/>
								</QuickViewInfoWrap>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<QuickViewInfoWrap label={t('Creator')}
												   highLightValue>{issue?.Creator}</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Submit Date')} highLightValue>
									{issue?.SubmitDt ? dayjs(issue?.SubmitDt).format(`${dateFormat} ${timeFormat}`) : '-'}
								</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Actual End')} highLightValue>
									{issue?.ActualEndDt ? dayjs(issue?.ActualEndDt).format(`${dateFormat} ${timeFormat}`) : '-'}
								</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Actual Start')} highLightValue>
									{issue?.ActualStartDt ? dayjs(issue?.ActualStartDt).format(`${dateFormat} ${timeFormat}`) : '-'}
								</QuickViewInfoWrap>
							</Grid>
							<Grid size={12}>
								<QuickViewInfoWrap
									label={t('Description')}
									highLightValue
								>
									{issue?.Description}
								</QuickViewInfoWrap>
							</Grid>
							<Grid size={12}>
								<QuickViewInfoWrap
									label={t('Summary')}
									highLightValue
									sx={{
										maxHeight: 200,
										overflow: 'auto'
									}}
								>
									<Box dangerouslySetInnerHTML={{ __html: issue?.Summary }}/>
								</QuickViewInfoWrap>
							</Grid>
						</Grid>
					</Box>
					<Box sx={{ marginTop: 2 }}>
						<Grid container spacing={2}>
							<Grid size={12}>
								<FormBoxTitle
									title={t('Customer Details')}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<QuickViewInfoWrap label={t('Customer')}
												   highLightValue>{issue?.CustName}</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Main Contact')}
												   highLightValue>{issue?.MainContactName}</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Main Contact Email')}
												   highLightValue>{issue?.MainContactEmail}</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Main Contact Phone')}
												   highLightValue>{issue?.MainContactPhone}</QuickViewInfoWrap>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<QuickViewInfoWrap label={t('Site')} highLightValue>{issue?.Site}</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Address')}
												   highLightValue>{issue?.Address}</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('FM Name')}
												   highLightValue>{issue?.FmName}</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('FM Email')}
												   highLightValue>{issue?.FmEmail}</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Contact')}
												   highLightValue>{issue?.ContactName}</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Contact Email')}
												   highLightValue>{issue?.ContactEmail}</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Applicant')}
												   highLightValue>{issue?.ContactNameText}</QuickViewInfoWrap>
							</Grid>
						</Grid>
					</Box>
					<Box sx={{ marginTop: 2 }}>
						<Grid container spacing={2}>
							<Grid size={12}>
								<FormBoxTitle
									title={t('Technical Details')}
								/>
							</Grid>
							<Grid size={12}>
								<QuickViewInfoWrap label={t('Service Category')}
												   highLightValue>{issue?.ItemCategDescription}</QuickViewInfoWrap>
								<QuickViewInfoWrap label={t('Service')}
												   highLightValue>{issue?.ItemDescription}</QuickViewInfoWrap>
								<PermissionHOC
									requiredPermissions={[quickview_view_assignee]}
								>
									<QuickViewInfoWrap label={t('Subcontractor')}
													   highLightValue>{issue?.Assignee}</QuickViewInfoWrap>
								</PermissionHOC>
								<PermissionHOC
									requiredPermissions={[quickview_view_technicians]}
								>
									<QuickViewInfoWrap label={t('Technicians')}
													   highLightValue>
										<Box sx={{ ...flexBoxSx, justifyContent: 'flex-start', gap: 0.5 }}>
											{issue?.IssueTechs?.map((i, index) => (
												<Box>{`${i.Name}${index < issue?.IssueTechs?.length - 1 ? ',' : ''}`}</Box>
											))}
										</Box>
									</QuickViewInfoWrap>
								</PermissionHOC>
							</Grid>
						</Grid>
					</Box>
					<Box sx={{ marginTop: 2 }}>
						<Grid container spacing={2}>
							<Grid size={{ xs: 12, sm: 12, md: 6, lg: 5 }}>
								<Box sx={{ marginTop: 2 }}>
									<Grid container spacing={2}>
										<Grid size={12}>
											<FormBoxTitle
												title={t('Status')}
											/>
										</Grid>
										<Grid size={12}>
											<QuickViewInfoWrap label={t('Issue Type')}
															   highLightValue>{issue?.Kind}</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Alternative Type')}
															   highLightValue>{issue?.Type2}</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Status')}>
												{issue?.Status && (
													<CardChip
														label={issue.Status}
														color={issue?.StatusColor}
														borderColor={issue?.StatusColor}
													/>
												)}
											</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Priority')}>
												{issue?.Priority && (
													<CardChip
														label={issue.Priority}
														color={issue?.PriotiryColor}
														borderColor={issue?.PriotiryColor}
													/>
												)}
											</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Dimension')}>
												{issue?.Type && (
													<CardChip
														label={issue.Type}
														color={issue?.TypeColor}
														borderColor={issue?.TypeColor}
													/>
												)}
											</QuickViewInfoWrap>
										</Grid>
									</Grid>
								</Box>
								<PermissionHOC
									requiredPermissions={[quickview_view_financial_info]}
								>
									<Box sx={{ marginTop: 2 }}>
										<Grid container spacing={2}>
											<Grid size={12}>
												<FormBoxTitle
													title={t('Financial Info')}
												/>
											</Grid>
											<Grid size={12}>
												<PermissionHOC
													requiredPermissions={[quickview_view_charge]}
												>
													<QuickViewInfoWrap
														label={t('Subcontractor Charge')}
														highLightValue
													>
														<NumericFormat
															thousandSeparator={thousandSeparator}
															decimalSeparator={decimalSeparator}
															displayType="text"
															value={issue?.ChargeCost ?? 0}
															decimalScale={2}
														/>
													</QuickViewInfoWrap>
												</PermissionHOC>
												<PermissionHOC
													requiredPermissions={[quickview_view_actual_charge]}
												>
													<QuickViewInfoWrap
														label={t('Customer Cost')}
														highLightValue
													>
														<NumericFormat
															thousandSeparator={thousandSeparator}
															decimalSeparator={decimalSeparator}
															displayType="text"
															value={issue?.ActualCost ?? 0}
															decimalScale={2}
														/>
													</QuickViewInfoWrap>
												</PermissionHOC>
											</Grid>
										</Grid>
									</Box>
								</PermissionHOC>
							</Grid>
							<Grid size={{ xs: 12, sm: 12, md: 6, lg: 7 }}>
								<Box sx={{ marginTop: 2 }}>
									<Grid container spacing={2}>
										<Grid size={12}>
											<FormBoxTitle
												title={t('Timeline')}
											/>
										</Grid>
										<Grid size={12}>
											<QuickViewInfoWrap label={t('Targeted Start')} highLightValue>
												{issue?.TargetedStartDt ? dayjs(issue?.TargetedStartDt).format(`${dateFormat}`) : '-'}
											</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Targeted Start Time')} highLightValue>
												{issue?.TargetedStartTm ? dayjs(issue?.TargetedStartTm, 'HH:mm:ss').format(`${timeFormat}`) : '-'}
											</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Actual Visit Date')} highLightValue>
												{issue?.ActualVisitDt ? dayjs(issue?.ActualVisitDt).format(`${dateFormat}`) : '-'}
											</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Actual Visit Time')} highLightValue>
												{issue?.ActualVisitTm ? dayjs(issue?.ActualVisitTm, 'HH:mm:ss').format(`${timeFormat}`) : '-'}
											</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Targeted Expiry')} highLightValue>
												{issue?.TargetedEndDt ? dayjs(issue?.TargetedEndDt).format(`${dateFormat}`) : '-'}
											</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Targeted Expiry Time')} highLightValue>
												{issue?.TargetedEndTm ? dayjs(issue?.TargetedEndTm, 'HH:mm:ss').format(`${timeFormat}`) : '-'}
											</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Est. Start')} highLightValue>
												{issue?.EstimStartDt ? dayjs(issue?.EstimStartDt).format(`${dateFormat}`) : '-'}
											</QuickViewInfoWrap>
											<QuickViewInfoWrap label={t('Est. Start Time')} highLightValue>
												{issue?.EstimStartTm ? dayjs(issue?.EstimStartTm, 'HH:mm:ss').format(`${timeFormat}`) : '-'}
											</QuickViewInfoWrap>
											<PermissionHOC
												requiredPermissions={[quickview_view_estimated_end]}
											>
												<QuickViewInfoWrap label={t('Est. Expiry')} highLightValue>
													{issue?.EstimStartDt ? dayjs(issue?.EstimStartDt).format(`${dateFormat}`) : '-'}
												</QuickViewInfoWrap>
												<QuickViewInfoWrap label={t('Est. Expiry Time')} highLightValue>
													{issue?.EstimDueTm ? dayjs(issue?.EstimDueTm, 'HH:mm:ss').format(`${timeFormat}`) : '-'}
												</QuickViewInfoWrap>
											</PermissionHOC>
										</Grid>
									</Grid>
								</Box>
							</Grid>
						</Grid>
					</Box>
					<Box sx={{ marginTop: 2, width: '100%', overflowX: 'auto !important' }}>
						<Grid container spacing={2}>
							<Grid size={{ xs: 12 }}>
								<FormBoxTitle
									title={t('Items')}
								/>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<ItemsTable items={issue?.Items} t={t} decimalSeparator={decimalSeparator}
											thousandsSeparator={thousandSeparator}/>
							</Grid>
						</Grid>
					</Box>
					<Box sx={{ marginTop: 2, width: '100%', overflowX: 'auto !important' }}>
						<Grid container spacing={2}>
							<Grid size={{ xs: 12 }}>
								<FormBoxTitle
									title={t('Assets')}
								/>
							</Grid>
							<Grid size={{ xs: 12 }}>
								<AssetsTable assets={issue?.Assets} t={t}/>
							</Grid>
						</Grid>
					</Box>
					<Box sx={{ marginTop: 2 }}>
						<Grid container spacing={2}>
							<Grid size={12}>
								<FormBoxTitle
									title={t('Comments')}
								/>
							</Grid>
							<Grid size={12}>
								<NotesTable notes={issue?.Notes} dateFormat={dateFormat} timeFormat={timeFormat} t={t}/>
							</Grid>
						</Grid>
					</Box>
					<Box sx={{ marginTop: 2 }}>
						<Grid container spacing={2}>
							<Grid size={12}>
								<FormBoxTitle
									title={t('Attachments')}
								/>
							</Grid>
							<Grid size={12}>
								<AttachmentsTable attachments={issue?.Attachments} dateFormat={dateFormat}
												  timeFormat={timeFormat} t={t}/>
							</Grid>
						</Grid>
					</Box>
				</PageContentWrapper>
				<Dialog
					open={openPO}
					onClose={()=>setOpenPO(false)}
					maxWidth="md"
					fullWidth
				>
					<PoQuickView
						Code={issue?.POCode}
						onClose={() => setOpenPO(false)}
						loadingIndex={loadingIndex}
						filters={filters}
						dataManager={dataManager}
						setRowSelectionModel={setRowSelectionModel}
					/>
				</Dialog>
				<Dialog
					open={openSOCust}
					onClose={()=>setOpenSOCust(false)}
					maxWidth="md"
					fullWidth
				>
					<SoCustQuickView
						Code={issue?.SOCode}
						onClose={()=>setOpenSOCust(false)}
					/>
				</Dialog>
				<Dialog
					open={openPOCust}
					onClose={()=>setOpenPOCust(false)}
					maxWidth="md"
					fullWidth
				>
					<PoCustQuickView
						Code={issue?.CPOCode}
						onClose={()=>setOpenPOCust(false)}
					/>
				</Dialog>
			</Box>
		</SkeletonLoaderWrapper>
	);
};

export default IssueQuickView